import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { SEO } from '../seo/SEO';
import { useI18n } from '../i18n';
import { useAuth } from '../auth';
import { AppHeader } from '../components/AppHeader';
import { useAppModal } from '../components/Modal';
import './ChallengePage.css';

const SOCKET_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface Friend {
  user_id: string;
  nickname: string;
  rating?: number;
}

export function ChallengePage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user, token } = useAuth();
  const { showAlert, modalElement } = useAppModal();

  const [friends, setFriends] = useState<Friend[]>([]);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState<Friend | null>(null);
  const [myRating, setMyRating] = useState(1000);
  const [sending, setSending] = useState(false);

  const socketRef = useRef<Socket | null>(null);
  const [playerId] = useState(() => {
    if (user?.id) return user.id;
    let id = localStorage.getItem('sudoku_player_id');
    if (!id) {
      id = 'p_' + Math.random().toString(36).slice(2, 10);
      localStorage.setItem('sudoku_player_id', id);
    }
    return id;
  });

  const nickname = user?.nickname || localStorage.getItem('sudoku_nickname') || 'Oyuncu';

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/stats/season`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const seasonal = d?.my_rating?.rating;
        if (typeof seasonal === 'number') { setMyRating(seasonal); return; }
        return fetch(`${API}/stats/me`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json())
          .then(d2 => { if (d2.rating) setMyRating(d2.rating); });
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!token) return;
    setLoadingFriends(true);
    fetch(`${API}/friends`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setFriends(data.friends || []); })
      .catch(() => {})
      .finally(() => setLoadingFriends(false));
  }, [token]);

  useEffect(() => {
    return () => { socketRef.current?.disconnect(); };
  }, []);

  const sendFriendChallenge = async () => {
    if (!user || !selectedFriend || sending) return;
    setSending(true);

    let friendRating = 1000;
    try {
      const res = await fetch(`${API}/stats/user/${selectedFriend.user_id}`);
      const data = await res.json();
      friendRating = data.stats?.rating || 1000;
    } catch { /* default */ }

    if (!token) return;
    const sock = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      auth: { token },
    });
    socketRef.current = sock;

    sock.on('connect', () => {
      sock.emit('challenge_friend', {
        player_id: playerId,
        user_id: user.id,
        nickname,
        rating: myRating,
        friend_user_id: selectedFriend.user_id,
        friend_rating: friendRating,
      });
    });

    sock.on('room_created', (room: { room_id: string; room_code: string }) => {
      localStorage.setItem('sudoku_active_room', JSON.stringify({
        room_id: room.room_id, room_code: room.room_code, nickname, player_id: playerId,
      }));
      navigate(`/room/${room.room_id}`);
    });

    sock.on('error', (data: { message: string }) => {
      sock.disconnect();
      socketRef.current = null;
      setSending(false);
      showAlert(data.message);
    });
  };

  return (
    <div className="challenge-page">
      <SEO path="/challenge" />
      <AppHeader onBack={() => navigate('/')} backLabel={t.common.backToHome} showFontToggle />
      {modalElement}

      <div className="challenge-content">
        <div className="challenge-header">
          <h1>{(t.home as any).rankedMenuFriend}</h1>
          <p>{(t.home as any).rankedMenuFriendDesc}</p>
          {myRating > 0 && (
            <div className="challenge-rating challenge-rating--inline">
              <span>{t.challenge.ratingLabel}: <strong>{myRating}</strong></span>
            </div>
          )}
        </div>

        {!user ? (
          <p className="challenge-login-msg">{t.challenge.loginRequired}</p>
        ) : loadingFriends ? (
          <p className="challenge-loading">{t.common.loading}</p>
        ) : friends.length === 0 ? (
          <p className="challenge-no-friends">{t.challenge.noFriends}</p>
        ) : (
          <div className="challenge-friend-list">
            <p className="challenge-select-label">{t.challenge.selectFriend}:</p>
            {friends.map(f => (
              <div
                key={f.user_id}
                className={`challenge-friend-item${selectedFriend?.user_id === f.user_id ? ' selected' : ''}`}
                onClick={() => setSelectedFriend(f)}
              >
                <span className="challenge-friend-avatar">{f.nickname[0].toUpperCase()}</span>
                <div className="challenge-friend-info">
                  <span className="challenge-friend-name">{f.nickname}</span>
                  {f.rating !== undefined && <span className="challenge-friend-rating">{f.rating} ELO</span>}
                </div>
                {selectedFriend?.user_id === f.user_id && <span className="challenge-friend-check">✓</span>}
              </div>
            ))}
            <button
              className="challenge-btn challenge-btn--ranked"
              disabled={!selectedFriend || sending}
              onClick={sendFriendChallenge}
            >
              {sending ? '...' : t.challenge.sendChallenge}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
